/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/Utilities.tsx
 * PURPOSE: "Tiện ích" launcher (web-sync checklist A.3) — grid of shortcuts to
 *          the employee self-service surfaces that already have live web
 *          routes (Tasks / Leave / Training / Change password), plus locked
 *          "Sắp có" placeholder tiles for everything else mobile's
 *          UtilitiesScreen shows that has no web destination yet, or whose
 *          backend is not actually finished (KPI — see ISS-EMP-2 note on the
 *          `kpi` tile below). Zero-Mock Policy: locked tiles never navigate,
 *          never point at a fabricated route.
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + Long-Running Agent Protocol)
 * DATE: 2026-08-15
 * COMPLIANCE: SDLC 6.5.0 + Sprint Governance Rule 3 (Zero-Mock Tolerance)
 * PILLAR: P1 (Stage 06 - BUILD) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance
 *
 * @business-context
 * - Domain: Employee Self-Service, Vietnamese F&B
 * - Impact: User Experience — mirrors mobile's "Tiện ích" tab (emp-49)
 * - Stakeholders: Employees (71)
 *
 * @technical-architecture
 * - Layer: Presentation Layer, Page Component
 * - Pattern: Static grid launcher, React Router navigate()
 * - Dependencies: React 18, React Router
 *
 * @cross-references
 * - Checklist: temp/WEB-SYNC-CHECKLIST-MOBILE-2026-08-15.md items A.3, A.9
 * - Mobile source of truth: mobile/src/screens/utilities/UtilitiesScreen.tsx
 * - Mobile tab config: mobile/src/navigation/BottomTabNavigator.tsx (TAB_CONFIG)
 * - Live destinations: src/pages/TasksProfessional.tsx, LeaveRequestModern.tsx,
 *   TrainingPortal.tsx, ChangePassword.tsx
 * - Locked-tile visual pattern precedent: src/pages/dashboards/EmployeeDashboard.tsx
 *   (quickLinkTile / quickLinkTileLocked / quickLinkTileBadge)
 * - ISS-EMP-2 (2026-08-17, @coder): `kpi` tile relocked (was `locked: false`,
 *   `path: '/kpi/self-assessment'`) — KPISelfAssessment.tsx calls
 *   `GET/POST /api/v2/kpi/employee/me/current|submit`, but
 *   backend/app/api/v2/endpoints/kpi.py only defines
 *   `/employee/{employee_id}/current|submit` with `employee_id: UUID` —
 *   there is no `/employee/me/*` convenience route, so the literal `"me"`
 *   fails UUID path-param validation → 422 on every load, confirmed by
 *   reading both files (not the FE's fault: no such route exists to call
 *   correctly). Even the real `{employee_id}` route has a second,
 *   independent bug: it types `current_employee` as `Optional[Employee]` via
 *   `Depends(get_current_tenant_employee)`, but that dependency returns
 *   `Tuple[str, Employee]` (see `get_current_employee` in
 *   `backend/app/core/tenant_context.py` for the correct bare-`Employee`
 *   wrapper it should have used instead) — `can_view_kpi()` then calls
 *   `.id` on a tuple, which would 500 even with a correct UUID. Mobile hit
 *   the same wall from a different angle (`UtilitiesScreen.tsx:24` — "cả 3
 *   API trong src/api/kpi.ts luôn 404") and already ships this tile
 *   `locked: true`; this fix makes web match mobile instead of showing a
 *   permanently-broken red error banner. KPISelfAssessment.tsx itself is
 *   left in place (route still registered in App.tsx), same as mobile
 *   leaves MyKPIDashboard.tsx/KPIEntryScreen.tsx in place with 0 call-sites
 *   — this is a backend/API-contract gap for @architect, not a UI fix.
 *   Full analysis: docs/05-test/13-test-issues/RELEASE-V0-ISSUES-CONSOLIDATED-2026-08-09.md §43.
 * - 2026-09-04 (structural-layout + button-proportion parity audit, same
 *   dimension already applied this session to AttendanceModern.tsx /
 *   ScheduleModern.tsx — read mobile/src/screens/utilities/UtilitiesScreen.tsx
 *   line-by-line against this file + Utilities.module.css):
 *   - FIXED: `.tile` (the launcher tiles below are real `<button>` elements,
 *     not decorative divs) used the CARD radius token instead of the
 *     TILE/button radius mobile actually uses — see Utilities.module.css
 *     file header + inline comment on `.tile` for the full citation
 *     (UtilitiesScreen.tsx:271 brandRadius.md=8px vs the 12px "cards" token
 *     this page had).
 *   - REVIEWED, KEPT — no Card-wrapper mismatch: mobile's UtilitiesScreen
 *     never Card-wraps the group/tile grid either (bare `View`s throughout,
 *     UtilitiesScreen.tsx:190-232) — this page's `.group`/`.grid` are
 *     likewise un-boxed. No AttendanceModern-style fix applies here.
 *   - REVIEWED, KEPT — header stays the plain `<h1>` + "Về Dashboard" button
 *     shape (not AttendanceModern's single-row ‹-back + state-driven title):
 *     that restructuring was specific to AttendanceModern's multi-state
 *     `screenState` stack producing duplicate in-body titles per state. This
 *     page renders once, has one title, no state stack — same reasoning
 *     ScheduleModern.tsx's 2026-09-04 audit note already recorded for its own
 *     single-state header. `.backButton`'s `var(--nqh-radius-lg)` also
 *     matches both sibling pages' `.backButton` (no mobile AppButton
 *     equivalent exists for this web-only "Về Dashboard" affordance — see
 *     ISS-EMP-9 above — so there is no brandRadius.md citation to apply to
 *     it, unlike `.tile`).
 *   - REVIEWED, KEPT — `.main` already spaces `.group` children via `gap`
 *     (Utilities.module.css `.main`), not one-off margins — already the
 *     convention AttendanceModern/ScheduleModern's `.main` were fixed
 *     *to*, so no change needed here.
 *   - REVIEWED, KEPT — `.tile` `min-height: 84px` already equals mobile's
 *     `tile.minHeight: 84` (UtilitiesScreen.tsx:269) exactly; icon size 22px
 *     matches `<Ionicons size={22}>` (UtilitiesScreen.tsx:215); the
 *     locked-badge "Sắp có" absolute-positioned top-right matches mobile's
 *     `Pill`+`tileBadge` placement (UtilitiesScreen.tsx:224-226,280-284).
 *   - REVIEWED, KEPT (out of scope) — mobile's locked tiles open a
 *     `LockedFeatureSheet` bottom-sheet explaining the lock
 *     (UtilitiesScreen.tsx:63-69,234-239); this page instead renders locked
 *     tiles `disabled` with no explanation sheet. That is a behavior/
 *     feature-completeness gap, not a structural-layout or button-proportion
 *     one — outside this audit's scope — and is already covered by this
 *     file's own Zero-Mock Policy note above (no fabricated navigation).
 *   - REVIEWED, KEPT (deliberate desktop adaptation, not drift) — exact
 *     spacing-token pixel values on `.main`/`.group`/`.grid`/`.tile` padding
 *     differ from mobile's raw px (e.g. desktop `.main` horizontal padding
 *     32px vs mobile's 16px; 4-column desktop grid vs mobile's always-2-
 *     column flex-wrap at phone width) — consistent with the same
 *     wider-viewport scaling ScheduleModern.tsx's 2026-09-04 note already
 *     judged reasonable for its grid cells, not unintentional drift. Radius
 *     was the one dimension flagged because it is a *categorical* token
 *     mismatch (card token vs tile token), not a scale difference.
 * - ISS-EMP-9 (2026-08-16): this route was created without EmployeeBottomNav —
 *   the only way out was the header's "Về Dashboard" button. Wired in the same
 *   shared component the other 4 employee routes use (Dashboard/Attendance/
 *   Schedule/Leave); .page's padding-bottom for nav clearance was already
 *   present in Utilities.module.css, just unused until now. "Tiện ích" tab
 *   activates automatically (EmployeeBottomNav's item path === '/utilities').
 *  */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import {
  CheckSquare,
  FileText,
  GitBranch,
  Building2,
  BarChart3,
  Calendar,
  Clock,
  CalendarDays,
  PartyPopper,
  GraduationCap,
  Edit3,
  Wallet,
  Banknote,
  KeyRound,
} from 'lucide-react';
import { EmployeeBottomNav } from '@/components/navigation/EmployeeBottomNav';
import styles from './Utilities.module.css';

interface UtilityTile {
  key: string;
  label: string;
  icon: LucideIcon;
  path: string | null;
  locked: boolean;
}

interface UtilityGroup {
  title: string;
  tiles: UtilityTile[];
}

// Mirrors mobile/src/screens/utilities/UtilitiesScreen.tsx grouping. Live
// tiles are the routes web-sync checklist A.3 names as already live: Tasks /
// Leave / Training / Change password. `kpi` was also listed live in A.3 but
// is relocked per ISS-EMP-2 above (backend endpoint mismatch, matches
// mobile's own lock). Everything else mobile shows is rendered locked/"Sắp
// có" — no invented routes (Zero-Mock Policy).
// Icon per tile mirrors mobile's Ionicons name for that same feature
// (mobile/src/screens/utilities/UtilitiesScreen.tsx): checkbox-outline,
// document-text-outline, git-branch-outline, business-outline,
// stats-chart-outline, calendar-outline, time-outline,
// calendar-number-outline, school-outline, create-outline, wallet-outline,
// cash-outline. `change-password` has no mobile-utilities equivalent (it's
// a web-only live destination per A.3) — kept in the same lucide-react set
// (KeyRound) for icon-type consistency across tiles.
const GROUPS: UtilityGroup[] = [
  {
    title: 'Công việc',
    tiles: [
      { key: 'tasks', label: 'Nhiệm vụ của tôi', icon: CheckSquare, path: '/tasks', locked: false },
      { key: 'requests', label: 'Yêu cầu của tôi', icon: FileText, path: null, locked: true },
      { key: 'process', label: 'Quy trình', icon: GitBranch, path: null, locked: true },
      { key: 'industry', label: 'Việc theo ngành', icon: Building2, path: null, locked: true },
    ],
  },
  {
    title: 'Nhân sự',
    tiles: [
      { key: 'leave', label: 'Nghỉ phép', icon: Calendar, path: '/leave-requests', locked: false },
      // ISS-EMP-2: relocked, backend endpoint contract gap (see file header) — not a UI bug
      { key: 'kpi', label: 'KPI tự đánh giá', icon: BarChart3, path: null, locked: true },
      // 2026-08-25 (user decision 5-tab + hub links): /attendance/history đã
      // mount từ web-sync A.4 — unlock tile này thay vì giữ locked.
      { key: 'attendance-history', label: 'Lịch sử chấm công', icon: Clock, path: '/attendance/history', locked: false },
      { key: 'leave-calendar', label: 'Lịch nghỉ', icon: CalendarDays, path: null, locked: true },
      // 2026-09-24 (SC-032-EMP-PUBLIC-HOLIDAYS, mới): lối vào PHỤ (lối vào
      // CHÍNH là section [10] clickable trong form Tạo đơn nghỉ phép,
      // LeaveRequestModern.tsx). Phân biệt rõ với "Lịch nghỉ" ngay trên:
      // đó là đơn nghỉ phép CỦA TÔI (còn khoá — X05 open), đây là ngày lễ
      // CHÍNH THỨC trong năm (chỉ đọc, đã build — không khoá).
      { key: 'holidays', label: 'Ngày lễ', icon: PartyPopper, path: '/public-holidays', locked: false },
    ],
  },
  {
    title: 'Đào tạo',
    tiles: [
      { key: 'training', label: 'Đào tạo', icon: GraduationCap, path: '/training', locked: false },
      { key: 'assessment-demo', label: 'Trả bài chấm demo', icon: Edit3, path: null, locked: true },
    ],
  },
  {
    title: 'Phúc lợi',
    tiles: [
      { key: 'nrp', label: 'Ví điểm NRP', icon: Wallet, path: null, locked: true },
      { key: 'bonus', label: 'Lịch sử thưởng', icon: Banknote, path: null, locked: true },
    ],
  },
  {
    title: 'Tài khoản',
    tiles: [
      { key: 'change-password', label: 'Đổi mật khẩu', icon: KeyRound, path: '/change-password', locked: false },
    ],
  },
];

export const Utilities: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerContent}>
          <h1 id="utilities-title" className={styles.title}>Tiện ích</h1>
          <button onClick={() => navigate('/dashboard/staff')} className={styles.backButton}>
            Về Dashboard
          </button>
        </div>
      </header>

      <main aria-labelledby="utilities-title" className={styles.main}>
        {GROUPS.map((group) => (
          <section key={group.title} className={styles.group}>
            <h2 className={styles.groupTitle}>{group.title}</h2>
            <div className={styles.grid} role="group" aria-label={group.title}>
              {group.tiles.map((tile) => {
                const Icon = tile.icon;
                return (
                  <button
                    key={tile.key}
                    type="button"
                    onClick={() => tile.path && navigate(tile.path)}
                    disabled={tile.locked}
                    aria-disabled={tile.locked}
                    className={`${styles.tile} ${tile.locked ? styles.tileLocked : ''}`}
                    data-testid={`utility-tile-${tile.key}`}
                  >
                    <Icon className={styles.tileIcon} aria-hidden="true" />
                    <span className={styles.tileLabel}>{tile.label}</span>
                    {tile.locked && <span className={styles.tileBadge}>Sắp có</span>}
                  </button>
                );
              })}
            </div>
          </section>
        ))}
      </main>

      <EmployeeBottomNav />
    </div>
  );
};

export default Utilities;
